import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";

import { MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";

const AVATAR_COLORS = [
  "#2f72f2",
  "#22c55e",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#0ea5e9",
];

function colorFor(name) {
  const str = name || "?";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + hash * 31;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function DoctorsTable({
  data,
  loading,
  onEdit,
  entityLabel,
  searchPlaceholder,
  actionTooltip,
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter((d) => (d.Name || "").toLowerCase().includes(q));
  }, [data, search]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        border: "1px solid #e8eef7",
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          px: 2.5,
          py: 1.25,
          borderBottom: "1px solid #e8eef7",
        }}
      >
        <Typography
          sx={{ fontWeight: 800, fontSize: "14px", color: "#0f172a" }}
        >
          {filtered.length} {entityLabel}
          {filtered.length === 1 ? "" : "s"} available
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#f8fbff",
            border: "1px solid #e1eaf6",
            borderRadius: "8px",
            px: 1.5,
            py: 0.5,
            minWidth: "200px",
          }}
        >
          <MagnifyingGlass size={18} weight="bold" color="#94a3b8" />
          <InputBase
            placeholder={searchPlaceholder}
            value={search}
            onChange={handleSearch}
            sx={{ fontSize: "14px", flex: 1 }}
          />
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: 0,
                  border: 0,
                  background: "#fbfdff",
                }}
              >
                No
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: 0,
                  border: 0,
                  background: "#fbfdff",
                }}
              >
                Doctor
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: 0,
                  border: 0,
                  background: "#fbfdff",
                }}
              >
                Registry status
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: 0,
                  border: 0,
                  background: "#fbfdff",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ py: 1.1 }}>
                    <Skeleton width={24} />
                  </TableCell>
                  <TableCell sx={{ py: 1.1 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Skeleton variant="circular" width={28} height={28} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="42%" />
                        <Skeleton width="56%" height={16} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.1 }}>
                    <Skeleton width={72} height={24} />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.1 }}>
                    <Skeleton
                      variant="rectangular"
                      width={28}
                      height={28}
                      sx={{ ml: "auto", borderRadius: "8px" }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ border: 0, py: 3 }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: "14px" }}>
                    No doctors found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row, idx) => (
                <TableRow
                  key={row.Id}
                  hover
                  sx={{
                    "&:last-child td": { border: 0 },
                    transition: "background 0.15s",
                    "&:hover": { background: "#f8fbff" },
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: "13px",
                      color: "#64748b",
                      borderBottom: "1px solid #eef3f9",
                      py: 0.9,
                    }}
                  >
                    {page * rowsPerPage + idx + 1}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #eef3f9" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: "13px",
                          fontWeight: 600,
                          bgcolor: colorFor(row.Name),
                        }}
                      >
                        {(row.Name || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#1e293b",
                          }}
                        >
                          {row.Name}
                        </Typography>
                        {(row.Email || row.Pan) && (
                          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                            {[row.Email, row.Pan].filter(Boolean).join(" | ")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #eef3f9" }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "999px",
                        background: "#ecfdf3",
                        color: "#16833a",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      Active
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ borderBottom: "1px solid #eef3f9" }}
                  >
                    <Tooltip title={actionTooltip}>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(row.Id)}
                        sx={{
                          color: "#2f72f2",
                          background: "#e8f0fe",
                          borderRadius: "8px",
                          "&:hover": { background: "#d8e7ff" },
                        }}
                      >
                        <PencilSimple size={18} weight="duotone" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: "1px solid #e8eef7" }}
      />
    </Paper>
  );
}

DoctorsTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  entityLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  actionTooltip: PropTypes.string,
};

DoctorsTable.defaultProps = {
  data: [],
  loading: false,
  entityLabel: "doctor",
  searchPlaceholder: "Search doctors...",
  actionTooltip: "Open doctor pricing",
};
